import { Accordion, AccordionHeader, Tooltip } from '@ui-kit'
import React, { useEffect } from 'react'
import {
  doesThisRowFailValidation,
  getElementFromAddedFields,
} from '../utils/form.utils'
import ElementRenderer from './ElementRenderer'
import {
  RowRendererInnerProps,
  RowRendererProps,
} from '../types/formRenderer.types'
import { FieldValues, Path, useWatch } from 'react-hook-form'
import { ViewRenderer } from './FormRenderer'
import {
  componentNames,
  MODE_CONSTANTS,
  NAMESPACES,
  PERMISSION_MODULES,
} from '../utils/constants'
import { RootState, useAppSelector } from '../utils/agent.store'
import {
  hasSensitiveDataPermission,
  permissionObject,
} from '../utils/helperFunctions'
import { LayoutRowType } from '@app-store'
import { isEmpty } from 'lodash'

const RowRenderer = <T extends FieldValues>({
  row,
  errors,
  mode,
  idx,
  clearErrors,
  control,
  setValue,
  register,
  layout,
  data,
  entityId,
  hideAccordion = false,
  formUniqueId = '',
  reregisterKey,
  fullLayout,
  tabIndexMap,
  dataId,
  subFormMetaObject,
  isStickySummary,
  fromIntakeReview,
  showPatientInformation,
  getValues,
}: RowRendererProps<T>) => {
  const RowValidationFailed = doesThisRowFailValidation(row, errors)
  const mustAddTabBuffer = tabIndexMap?.tabIndexOffset ?? 0
  const tabIndexes = tabIndexMap?.tabIndex
  const allEntities = useAppSelector(
    (state: RootState) => state.entity.entitiesByName
  )
  const globalPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.globalPermissions
  )

  const rowClassName = () => {
    if (RowValidationFailed) {
      return '!border-l-error-500'
    } else if (isStickySummary) {
      return 'bg-secondary-25'
    }
  }
  const isEntityGlobal = (row?: LayoutRowType) => {
    const entityKey = fullLayout?.entity_name
    const entity = allEntities[entityKey ?? '']
    if (isEmpty(row?.container_behavior)) {
      return entity?.source_type === NAMESPACES.GLOBAL_KEY
    } else {
      return (
        row?.container_behavior?.toUpperCase() ===
        NAMESPACES?.GLOBAL_KEY?.toUpperCase()
      )
    }
  }

  const hasGlobalPermission = () => {
    if (fullLayout?.entity_name === 'plans' && mode === 'add') return true
    return (
      (isEntityGlobal(row) &&
        permissionObject.Manage_Global_Schema(
          globalPermissions,
          PERMISSION_MODULES.MANAGE_GLOBAL_DATA
        )) ||
      !isEntityGlobal(row)
    )
  }
  return hideAccordion ? (
    <div>
      {row.label ? (
        <div className="text-base/[1.375rem] font-medium pb-3">{row.label}</div>
      ) : null}
      <RowRenderInner
        row={row}
        errors={errors}
        mode={mode}
        idx={idx}
        clearErrors={clearErrors}
        control={control}
        setValue={setValue}
        register={register}
        layout={layout}
        data={data}
        entityId={entityId}
        formUniqueId={formUniqueId}
        reregisterKey={reregisterKey}
        hideAccordion={hideAccordion}
        fullLayout={fullLayout}
        dataId={dataId}
        subFormMetaObject={subFormMetaObject}
        fromIntakeReview={fromIntakeReview}
        showPatientInformation={showPatientInformation}
        getValues={getValues}
      />
    </div>
  ) : (
    <Accordion
      heading={
        !isStickySummary ? (
          <AccordionHeader>
            <div className="flex items-center gap-3">
              <span className="">{row.label || NAMESPACES.INFORMATION}</span>
              {(() => {
                const isGlobal =
                  isEntityGlobal(row) && !row?.meta?.disable_global_client

                const isClient =
                  row?.container_behavior?.toLowerCase() ===
                    NAMESPACES?.CLIENT?.toLowerCase() &&
                  !row?.meta?.disable_global_client

                if (isGlobal) {
                  return (
                    <span className="flex items-center bg-secondary-50 border-secondary-25 border h-5 rounded-sm px-2 py-1 text-accent-600 text-xxs">
                      {NAMESPACES.GLOBAL}
                    </span>
                  )
                }

                if (isClient && !isEmpty(row?.container_behavior)) {
                  return (
                    <span className="flex items-center bg-progress-25 border-taskStatus-300 border h-5 rounded-sm px-2 py-1 text-accent-600 text-xxs">
                      {NAMESPACES.CLIENT}
                    </span>
                  )
                }

                return null
              })()}
            </div>
          </AccordionHeader>
        ) : (
          <AccordionHeader>
            <div className="text-secondary-500 mr-2">{row?.label}</div>
          </AccordionHeader>
        )
      }
      className={rowClassName()}
      triggerOpen={RowValidationFailed}
      isDefaultActive={[0, 1, 2].includes(idx || 0)}
      id={row.rowId}
      tabIndex={
        (tabIndexes?.[`${row?.rowId}-${row.label}`?.replace(' ', '')] ?? 0) +
        mustAddTabBuffer
      }
      alwaysOpen={isStickySummary}
    >
      <RowRenderInner
        row={row}
        errors={errors}
        mode={hasGlobalPermission() ? mode : 'view'}
        idx={idx}
        clearErrors={clearErrors}
        control={control}
        setValue={setValue}
        register={register}
        layout={layout}
        data={data}
        entityId={entityId}
        formUniqueId={formUniqueId}
        hideAccordion={hideAccordion}
        reregisterKey={reregisterKey}
        fullLayout={fullLayout}
        tabIndexMap={tabIndexMap}
        dataId={dataId}
        subFormMetaObject={subFormMetaObject}
        fromIntakeReview={fromIntakeReview}
        showPatientInformation={showPatientInformation}
        getValues={getValues}
      />
    </Accordion>
  )
}

const RowRenderInner = <T extends FieldValues>({
  row,
  errors,
  mode,
  clearErrors,
  control,
  setValue,
  register,
  layout,
  data,
  entityId,
  formUniqueId = '',
  hideAccordion = false,
  reregisterKey,
  fullLayout,
  tabIndexMap,
  dataId,
  subFormMetaObject,
  fromIntakeReview,
  getValues,
  showPatientInformation,
}: RowRendererInnerProps<T>) => {
  const tabIndexes = tabIndexMap?.tabIndex
  const mustAddTabBuffer = tabIndexMap?.tabIndexOffset ?? 0
  const pateintDetails = useAppSelector(
    (state) => state?.patientData?.patientData
  )
  const subscriberRelation = useWatch({
    name: 'subscriber_relationship_to_patient',
  })

  useEffect(() => {
    if (showPatientInformation && subscriberRelation?.id === 105) {
      setValue(
        'subscriber_first_name' as Path<T>,
        pateintDetails?.first_name ?? ''
      )
      setValue(
        'subscriber_last_name' as Path<T>,
        pateintDetails?.last_name ?? ''
      )
      setValue('subscriber_dob' as Path<T>, pateintDetails?.date_of_birth ?? '')
    }
  }, [subscriberRelation])

  return (
    <div className={`row flex gap-5 w-full ${hideAccordion ? '' : 'p-4'}`}>
      {row?.columns?.map((col) => {
        return (
          <div
            key={col.columnId}
            className="flex flex-col col gap-5 w-full min-w-[11.69rem]"
          >
            {col.elements.map((el) => {
              const element = getElementFromAddedFields(
                el,
                entityId,
                layout?.added_fields
              )
              const isSensitiveField =
                el?.keyName === 'is_client_controlled' &&
                !hasSensitiveDataPermission()
              return (
                <>
                  {mode === MODE_CONSTANTS.VIEW &&
                    element?.elementName !== componentNames.FILE_UPLOAD && (
                      <ViewRenderer
                        key={el.id}
                        element={element}
                        tabIndex={
                          el?.elementId && tabIndexes
                            ? tabIndexes?.[el?.elementId] + mustAddTabBuffer
                            : undefined
                        }
                        control={control}
                        layoutId={fullLayout?.id}
                        layout={fullLayout}
                      />
                    )}
                  <div
                    className={
                      mode === MODE_CONSTANTS.VIEW &&
                      element?.elementName !== componentNames.FILE_UPLOAD
                        ? 'hidden'
                        : ''
                    }
                  >
                    <Tooltip content={element.meta.tooltip_text ?? ''}>
                      <React.Fragment key={reregisterKey}>
                        <div className="hidden keyName">{element.keyName}</div>
                        <div className="hidden elementName">
                          {element.elementName}
                        </div>
                        <ElementRenderer
                          dataId={dataId}
                          data={data ?? []}
                          key={el.id}
                          element={{
                            ...element,
                            meta: {
                              ...element.meta,
                              default_value:
                                mode === MODE_CONSTANTS.ADD
                                  ? element.meta.default_value
                                  : '',
                              defaultValue:
                                mode === MODE_CONSTANTS.ADD
                                  ? element.meta.default_value
                                  : '',
                              disabled: isSensitiveField || element?.isDisabled,
                            },
                            tabIndex:
                              el?.elementId && tabIndexes
                                ? tabIndexes?.[el?.elementId] + mustAddTabBuffer
                                : undefined,
                          }}
                          register={register}
                          errors={errors}
                          setValue={setValue}
                          control={control}
                          clearErrors={clearErrors}
                          mode={mode}
                          formUniqueId={formUniqueId}
                          fullLayout={fullLayout}
                          subFormMetaObject={subFormMetaObject}
                          fromIntakeReview={fromIntakeReview}
                          getValues={getValues}
                        />
                      </React.Fragment>
                    </Tooltip>
                  </div>
                </>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default RowRenderer
